import { isRowCorrectable, isRowCreatableFrom, isRowEditable } from './invoice-row-eligibility';
import { canonicalInvoiceStatus } from './invoice-status';

import type { InvoiceRow } from '../types';

// ── The shared invoice action model ─────────────────────────────────────────
//
// One place answers "which actions does this invoice offer right now", keyed
// on the invoice's state (status, direction, kind, placeholder). The list row
// and the detail drawer both render their action sets from this model, so the
// two surfaces cannot drift: an action available on one is available on the
// other, and a state-invalid action (Issue on an Issued invoice) is absent on
// both rather than present-but-erroring.
//
// The model decides availability only. Each surface still owns how an action
// renders (button vs icon, tooltip on the disabled state) and which dialog or
// mutation it wires up.

export type InvoiceActionKey =
  | 'edit'
  | 'delete'
  | 'correct'
  | 'create_next'
  | 'review'
  | 'issue'
  | 'cancel'
  | 'void'
  | 'record_payment'
  | 'link_cash'
  | 'complete_placeholder';

// Statuses on which money can be recorded or linked. The eligible set already
// excludes Void/Cancelled.
const PAYMENT_STATUSES: ReadonlySet<string> = new Set(['Issued', 'Partially Paid', 'Paid']);

// Draft-scope changes: Edit and Delete share the same gate (never-issued,
// non-terminal); the server additionally blocks deleting past live payments.
function changeActions(invoice: InvoiceRow): InvoiceActionKey[] {
  const actions: InvoiceActionKey[] = [];
  if (isRowEditable(invoice)) actions.push('edit', 'delete');
  if (isRowCorrectable(invoice)) actions.push('correct');
  if (isRowCreatableFrom(invoice)) actions.push('create_next');
  return actions;
}

// Lifecycle: a Draft can be reviewed, issued (recorded on the buy side), or
// cancelled; an Issued / Partially Paid invoice can be voided.
function lifecycleActions(invoice: InvoiceRow): InvoiceActionKey[] {
  const status = canonicalInvoiceStatus(invoice.status);
  if (status === 'Draft') return ['review', 'issue', 'cancel'];
  if (status === 'Issued' || status === 'Partially Paid') return ['void'];
  return [];
}

function paymentActions(invoice: InvoiceRow): InvoiceActionKey[] {
  const status = canonicalInvoiceStatus(invoice.status);
  const actions: InvoiceActionKey[] = [];
  if (status !== null && PAYMENT_STATUSES.has(status)) {
    actions.push('record_payment');
  }
  // Linking a cash event needs the direction + currency the matcher keys on,
  // and stops once the invoice is fully Paid.
  const linkable = status === 'Issued' || status === 'Partially Paid';
  if (linkable && invoice.invoice_direction !== null && invoice.currency !== null) {
    actions.push('link_cash');
  }
  return actions;
}

export function invoiceActionsFor(invoice: InvoiceRow): ReadonlySet<InvoiceActionKey> {
  return new Set<InvoiceActionKey>([
    ...changeActions(invoice),
    ...lifecycleActions(invoice),
    ...paymentActions(invoice),
    ...(invoice.is_placeholder ? (['complete_placeholder'] as const) : []),
  ]);
}

export function invoiceActionAvailable(invoice: InvoiceRow, action: InvoiceActionKey): boolean {
  return invoiceActionsFor(invoice).has(action);
}

// ── Groups and consolidated verbs ───────────────────────────────────────────
//
// The eleven raw actions fall into four first-class groups, and collapse to
// six user-facing verbs. The underlying state machine is unchanged — a verb
// is a state-routed entry point over the raw actions:
//
//   Content    — what the document says: Change values routes to edit (Draft),
//                correct (issued payable), or complete (placeholder stub).
//   Lifecycle  — its status: Issue (with review folded in as step 1), Discard
//                (cancel a Draft — auditable — or soft-delete a never-issued
//                imported row), and Void. Void stays its own verb: voiding an
//                issued document leaves its legal number as a gap, which is a
//                real accounting distinction, not clutter.
//   Settlement — money on it: Add payment covers manual entry and matching a
//                cash-ledger event.
//   Chain      — related documents: New follow-on (create-next).

export type InvoiceActionGroup = 'content' | 'lifecycle' | 'settlement' | 'chain';

export const INVOICE_ACTION_GROUP: Readonly<Record<InvoiceActionKey, InvoiceActionGroup>> = {
  edit: 'content',
  complete_placeholder: 'content',
  correct: 'content',
  review: 'lifecycle',
  issue: 'lifecycle',
  cancel: 'lifecycle',
  delete: 'lifecycle',
  void: 'lifecycle',
  record_payment: 'settlement',
  link_cash: 'settlement',
  create_next: 'chain',
};

export type InvoiceVerbKey =
  | 'change_values'
  | 'issue'
  | 'discard'
  | 'void'
  | 'add_payment'
  | 'create_next';

export interface InvoiceVerb {
  readonly verb: InvoiceVerbKey;
  readonly group: InvoiceActionGroup;
  // The raw action the verb routes to in the invoice's current state.
  readonly routesTo: InvoiceActionKey;
  // Every raw action the verb spans right now (e.g. Add payment offers both
  // manual entry and cash-event matching when both are available).
  readonly actions: readonly InvoiceActionKey[];
}

function presentOf(
  actions: ReadonlySet<InvoiceActionKey>,
  candidates: readonly InvoiceActionKey[],
): InvoiceActionKey[] {
  return candidates.filter((action) => actions.has(action));
}

// Routing precedence per verb. Content: completing a placeholder stub is the
// meaningful change even while the row is also Draft-editable. Discard:
// cancelling keeps an auditable Cancelled record, so it wins over the soft
// delete, which remains the route for never-issued rows past Draft (imported
// data). Payment: manual entry is the primary; cash-event matching rides
// along while linkable.
const VERB_ROUTES: readonly (readonly [InvoiceVerbKey, readonly InvoiceActionKey[]])[] = [
  ['change_values', ['complete_placeholder', 'edit', 'correct']],
  ['issue', ['issue']],
  ['discard', ['cancel', 'delete']],
  ['void', ['void']],
  ['add_payment', ['record_payment', 'link_cash']],
  ['create_next', ['create_next']],
];

export function invoiceVerbsFor(invoice: InvoiceRow): readonly InvoiceVerb[] {
  const actions = invoiceActionsFor(invoice);
  const verbs: InvoiceVerb[] = [];
  for (const [verb, candidates] of VERB_ROUTES) {
    const present = presentOf(actions, candidates);
    const routesTo = present[0];
    if (routesTo === undefined) continue;
    // Review is not its own verb: it is step 1 of the Issue flow, so the
    // issue verb spans it whenever the draft is reviewable.
    const spanned = verb === 'issue' ? presentOf(actions, ['review', 'issue']) : present;
    verbs.push({ verb, group: INVOICE_ACTION_GROUP[routesTo], routesTo, actions: spanned });
  }
  return verbs;
}

export function invoiceVerbFor(invoice: InvoiceRow, verb: InvoiceVerbKey): InvoiceVerb | null {
  return invoiceVerbsFor(invoice).find((entry) => entry.verb === verb) ?? null;
}
